import { useEffect, useRef, useState } from 'react';
import { fetchDocRecord, runDocumentAgain, type DeadLetterDocView, type DocRecordView } from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { formatShortDate } from '../lib/format-date';
import { InfoTip } from '../lib/info-tip';
import { ResultJsonActions } from '../lib/result-json-actions';
import { documentTypeLabel, failureClassHeading, labels } from '../lib/labels';
import { RoleNote, useGate } from '../lib/session';
import { SourceFileViewer } from '../lib/source-file-viewer';
import { AuditTrail, DetectionBlock, FieldTable, GateReportBlock, markedCells, RawFiles, RepeatingGroupTable, shownFields, VerdictLine } from './doc-parts';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'not-found' } | { status: 'ok'; view: DocRecordView } | { status: 'dead-letter'; view: DeadLetterDocView };

/**
 * `#doc/<id>` — the compliance view. Values render exactly as
 * `GET /api/doc` returns them: no formatting, no rounding, no date parsing. Read-only; the two
 * guarded writes this screen links to (Pin/unpin, on `#extraction-rules`) live on their own
 * screens.
 *
 * Long Document (`design.md` § Macrostructure families): one object, read top to bottom, with
 * inline section heads and a spec-sheet field table. This screen gets pasted into a compliance
 * thread, so it breathes more than the register screens do and every pipeline value is mono —
 * whoever reads the screenshot needs to compare characters, not admire the typography.
 */
export function DocRecord({ id }: { id: string }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [showRaw, setShowRaw] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const [reloads, setReloads] = useState(0);
  const [started, setStarted] = useState('');
  const action = useActionError();
  const gate = useGate('administer');

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    setShowFile(false);
    fetchDocRecord(id)
      .then((view) => {
        if (cancelled) return;
        if (!view) setState({ status: 'not-found' });
        else setState('deadLetter' in view ? { status: 'dead-letter', view } : { status: 'ok', view });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloads]);

  async function runAgain(docId: string) {
    const answers = await ask({
      ...labels.docRecord.runAgainConfirm(docId),
      confirmLabel: labels.docRecord.runAgain,
      tone: 'guard',
    });
    if (!answers) return;
    setStarted('');
    let message = '';
    const ok = await action.run(async () => {
      const result = await runDocumentAgain(docId);
      message = labels.docRecord.runAgainStarted(result.docId, result.attempt);
    });
    if (!ok) return;
    setStarted(message);
    setReloads((count) => count + 1);
  }

  return (
    <div>
      <div className="page-head page-head--actions">
        {/* The id is known from the route before any fetch resolves, so the heading is never
         *  empty during loading — the same deep-linkable-immediately property the console
         *  asks of every route. */}
        <h2>{state.status === 'ok' ? `${documentTypeLabel(state.view.type)} · ${state.view.docId}` : id}</h2>
        {state.status === 'ok' && (
          /* QA-84: the page actions sit at the end edge of the title line. */
          <div className="action-row end-edge">
            <button type="button" className="btn" onClick={() => setShowRaw((prev) => !prev)}>
              {showRaw ? labels.docRecord.hideRaw : labels.docRecord.showRaw}
            </button>
            <ResultJsonActions view={state.view} />
            {/* The page exists only for a document with a completed result, the one case Run again accepts (QA-70). */}
            {/* QA-150: while review, a random check, or a parked handoff holds the document, Run again
             *  would fail with 409, so the page says who holds it instead. */}
            {state.view.heldBy ? (
              <span className="action-row__note">
                {labels.docRecord.held[state.view.heldBy].text}
                <InfoTip label={labels.docRecord.runAgain} text={labels.docRecord.held[state.view.heldBy].tip} />
              </span>
            ) : (
              <>
                <RoleNote action="administer" />
                <button type="button" className="btn" onClick={() => void runAgain(state.view.docId)} {...gate.props}>
                  {labels.docRecord.runAgain}
                </button>
              </>
            )}
          </div>
        )}
        {state.status === 'dead-letter' && (
          /* QA-163: the one page action of a document with no result: its stored file. */
          <div className="action-row end-edge">
            <button
              type="button"
              className="btn"
              aria-expanded={showFile}
              aria-controls="doc-source-file"
              onClick={() => setShowFile((prev) => !prev)}
            >
              {showFile ? labels.sourceFile.hide : labels.sourceFile.show}
            </button>
          </div>
        )}
      </div>
      {action.notice}
      {started && (
        <p className="notice notice--good" role="status">
          {started}
        </p>
      )}
      {state.status === 'loading' && <p className="notice">{labels.docRecord.loading}</p>}
      {state.status === 'error' && (
        <p className="notice notice--crit" role="alert">
          {labels.docRecord.loadFailed}
        </p>
      )}
      {state.status === 'not-found' && <p className="notice">{labels.docRecord.notFound}</p>}
      {state.status === 'dead-letter' && (
        <>
          <DeadLetterBlock view={state.view} />
          {showFile && (
            <section
              className="subpanel"
              id="doc-source-file"
              aria-label={labels.sourceFile.frameTitle(state.view.docId)}
            >
              <SourceFileViewer docId={state.view.docId} />
            </section>
          )}
        </>
      )}
      {state.status === 'ok' && (
        <DocBody view={state.view} showRaw={showRaw} />
      )}
    </div>
  );
}

/** QA-86: a document with no result that is on Not processed. No Run again: it has no result (QA-83). */
function DeadLetterBlock({ view }: { view: DeadLetterDocView }) {
  const { failureClass, ignored, filedType, detectedType, reason, timestamp, attempt, acknowledgedBy } = view.deadLetter;
  const group = failureClassHeading(failureClass);
  const text = labels.docRecord.deadLetter;
  return (
    <section className="subpanel">
      <h3>{ignored ? text.headingIgnored(group) : text.heading(group)}</h3>
      {ignored && <p>{text.ignoredBy(acknowledgedBy ?? '')}</p>}
      <p>{labels.notProcessed.classWhatToDo[failureClass]}</p>
      {detectedType && <p>{text.filedAs(documentTypeLabel(filedType), documentTypeLabel(detectedType))}</p>}
      {reason && (
        <p>
          {text.reason}: <span className="mono">{reason}</span>
        </p>
      )}
      <p>
        {text.since}: <span className="mono">{formatShortDate(timestamp)}</span> · {text.attempt}:{' '}
        <span className="mono">{attempt}</span>
      </p>
      <a href={ignored ? '#not-processed' : `#not-processed?failureClass=${encodeURIComponent(failureClass)}`}>{text.seeIt}</a>
    </section>
  );
}

function DocBody({ view, showRaw }: { view: DocRecordView; showRaw: boolean }) {
  const rawHeadingRef = useRef<HTMLHeadingElement>(null);
  // QA-113: the cells the verdict line names; each gets the flagged tint and takes focus.
  const marked = markedCells(view);

  useEffect(() => {
    if (showRaw) rawHeadingRef.current?.focus();
  }, [showRaw]);

  return (
    <>
      {/* QA-79: the verdict and its reason come first, under the title, not after the fields. */}
      <VerdictLine view={view} />
      <DetectionBlock view={view} />
      <FieldTable fields={shownFields(view)} marked={marked} />
      {view.repeatingGroups.map((group) => (
        <RepeatingGroupTable key={group.name} group={group} marked={marked} />
      ))}
      <GateReportBlock view={view} showReason={false} />
      <AuditTrail view={view} />
      {showRaw && <RawFiles view={view} headingRef={rawHeadingRef} />}
    </>
  );
}
